import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { toast } from "../../components/ui-components/Toast.jsx";
import { primaryButtonCls, secondaryButtonCls } from "../../components/ui-components/buttonStyles.js";
import { validateRecipients } from "../reports/scheduleUtils.js";
import { createBoardReport, listBoardReportTemplates, createRun } from "./document/boardReportApi.js";
import ReportDetailsFields from "./ReportDetailsFields.jsx";

// defaultPeriod - the last completed month (YYYY-MM), a sensible default for a monthly summary so a
// new report is valid the moment the form opens rather than starting on an empty required field.
function defaultPeriod() {
  return dayjs().subtract(1, "month").format("YYYY-MM");
}

function monthRange(p) {
  const base = dayjs(`${p}-01`);
  return [base.startOf("month"), base.endOf("month")];
}

/**
 * BoardReportConfig - the CREATE form for a new board report (/board-reports/new/config). Editing an
 * existing report's details now lives in the report home's Settings tab (panels/SettingsPanel),
 * which shares the same ReportDetailsFields. Recipients are optional (a report is a template you
 * preview, approve and edit before it's ever sent). After creating, we land on the new report's home
 * so the natural next step (generate a run) is one click away.
 */
const BoardReportConfig = () => {
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [type, setType] = useState("monthly_summary");
  const [templates, setTemplates] = useState([]);
  const [rangeStart, setRangeStart] = useState(() => monthRange(defaultPeriod())[0]);
  const [rangeEnd, setRangeEnd] = useState(() => monthRange(defaultPeriod())[1]);
  const [recipients, setRecipients] = useState([]);
  const [errors, setErrors] = useState({});
  // Which submit button is in flight, so each shows its own "Creating…" state:
  // null | "create" (Create report) | "create-run" (Create report and run).
  const [pending, setPending] = useState(null);

  // Load the template catalog for the picker; on failure the form still works with the default
  // monthly_summary type (no picker crash - see brief).
  useEffect(() => {
    listBoardReportTemplates()
      .then(setTemplates)
      .catch(() => setTemplates([]));
  }, []);

  const period = rangeStart ? rangeStart.format("YYYY-MM") : "";

  const validate = () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = "Name is required.";
    if (!period) {
      nextErrors.period = "Period is required.";
    } else if (!/^\d{4}-\d{2}$/.test(period)) {
      nextErrors.period = "Use the YYYY-MM format.";
    }
    if (recipients.length > 0) {
      const validation = validateRecipients(recipients.map((r) => r.email));
      if (!validation.isValid) {
        nextErrors.recipients = "One or more recipient emails are invalid.";
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Create the report; when andRun is true, immediately generate its first run and
  // jump to that run's review screen (skipping the report home). Otherwise land on
  // the report home so the user can generate a run when ready.
  const submit = async (andRun) => {
    if (!validate()) return;
    const payload = {
      name: name.trim(),
      type,
      period,
      scope: {},
      recipients: recipients.map((r) => ({ email: r.email, name: r.name || "" })),
      manifest_override: {},
    };
    setPending(andRun ? "create-run" : "create");
    try {
      const created = await createBoardReport(payload);
      const reportId = created?.id;

      if (andRun && reportId != null) {
        try {
          const run = await createRun(reportId);
          toast.success("Board report created");
          navigate(`/board-reports/${reportId}/runs/${run.id}/review`);
          return;
        } catch (runErr) {
          // The report was created; only the run failed. Land on the report home
          // so the user can retry the run, and say what happened.
          toast.error("Report created, but starting the run failed. You can run it from the report.");
          navigate(`/board-reports/${reportId}`);
          return;
        }
      }

      toast.success("Board report created");
      navigate(reportId != null ? `/board-reports/${reportId}` : "/board-reports");
    } catch (err) {
      toast.error("Failed to create board report");
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background-grey">
      <AdministrationHeader title="New board report" showPeriodSelector={false} />

      <div className="flex-1 overflow-auto">
        <div className="max-w-2xl p-8 space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-text">New board report</h2>
            <p className="mt-1 text-sm text-text-2">
              A monthly Cloud &amp; AI financial summary for your board. You can change the content,
              recipients and schedule any time after creating it.
            </p>
          </div>

          {templates.some((t) => t.available !== false) && (
            <div className="bg-card border border-card-bd rounded-card p-6 space-y-4">
              <div>
                <span className="block text-sm font-medium text-text">Template</span>
                <p className="mt-1 text-sm text-text-2">Choose what this board report covers.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {templates
                  .filter((t) => t.available !== false)
                  .map((t) => {
                    const selected = type === t.type;
                    return (
                      <button
                        key={t.type}
                        type="button"
                        onClick={() => setType(t.type)}
                        aria-pressed={selected}
                        className={`text-left p-4 rounded-ctrl border transition-colors cursor-pointer ${
                          selected
                            ? "border-primary ring-2 ring-primary bg-background-grey"
                            : "border-card-bd bg-card hover:border-primary"
                        }`}
                      >
                        <span className="block text-sm font-medium text-text">{t.name}</span>
                        <p className="mt-1 text-xs text-text-2">{t.description}</p>
                        {Array.isArray(t.pages) && t.pages.length > 0 && (
                          <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-text-3">
                            {t.pages.length} page{t.pages.length === 1 ? "" : "s"}
                          </p>
                        )}
                      </button>
                    );
                  })}
              </div>
            </div>
          )}

          <ReportDetailsFields
            name={name}
            onNameChange={setName}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            setRangeStart={setRangeStart}
            setRangeEnd={setRangeEnd}
            recipients={recipients}
            onRecipientsChange={setRecipients}
            errors={errors}
          />

          <div className="flex items-center gap-3">
            <button
              type="button"
              className={secondaryButtonCls}
              onClick={() => navigate("/board-reports")}
              disabled={pending != null}
            >
              Cancel
            </button>
            <button
              type="button"
              className={secondaryButtonCls}
              onClick={() => submit(false)}
              disabled={pending != null}
            >
              {pending === "create" ? "Creating…" : "Create report"}
            </button>
            <button
              type="button"
              className={primaryButtonCls}
              onClick={() => submit(true)}
              disabled={pending != null}
            >
              {pending === "create-run" ? "Creating…" : "Create report and run"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardReportConfig;
